/**
 * HTTP Client wrapper for all API calls
 * Automatically adds JWT token to all requests
 * Handles response errors globally
 */

import authService from './authService';
import { ENVIRONMENT } from '../config/properties';

/**
 * Execute HTTP request with automatic JWT token injection
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} Fetch response
 */
const httpClient = {
  /**
   * GET request
   */
  get: async (url, options = {}) => {
    return httpClient.request(url, {
      ...options,
      method: 'GET',
    });
  },

  /**
   * POST request
   */
  post: async (url, body, options = {}) => {
    return httpClient.request(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PUT request
   */
  put: async (url, body, options = {}) => {
    return httpClient.request(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete: async (url, options = {}) => {
    return httpClient.request(url, {
      ...options,
      method: 'DELETE',
    });
  },

  /**
   * PATCH request
   */
  patch: async (url, body, options = {}) => {
    return httpClient.request(url, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * Core request method that handles JWT injection and error handling
   */
  request: async (url, options = {}) => {
    try {
      // Ensure URL is absolute
      const absoluteUrl = url.startsWith('http') ? url : `${ENVIRONMENT.apiBaseUrl}${url}`;

      // Get default headers with JWT token
      const defaultHeaders = httpClient.getHeaders();

      // Merge headers (user-provided headers override defaults)
      const mergedHeaders = {
        ...defaultHeaders,
        ...(options.headers || {}),
      };

      // Request details
      const method = options.method || 'GET';

      // Execute request with credentials support
      const response = await fetch(absoluteUrl, {
        ...options,
        headers: mergedHeaders,
        credentials: 'include', // Include credentials (cookies, auth headers)
        mode: 'cors', // Enable CORS
      });

      // Handle HTTP errors
      if (!response.ok) {
        let errorText = '';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorJson = await response.json();
            errorText = JSON.stringify(errorJson, null, 2);
          } else {
            errorText = await response.text();
          }
        } catch (e) {
          errorText = `Status ${response.status}`;
        }

        console.error(
          `[httpClient] ${method} ${absoluteUrl} - Status: ${response.status}`,
          errorText
        );

        // Handle 401 Unauthorized - token expired or invalid
        if (response.status === 401) {
          console.warn('[httpClient] Received 401 Unauthorized - clearing auth data');
          authService.logout();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }

        // Handle 403 Forbidden
        if (response.status === 403) {
          console.error('[httpClient] 403 Forbidden error:', errorText);
          throw new Error(`Access denied (403). Details: ${errorText}`);
        }

        // Handle other errors
        if (response.status >= 500) {
          throw new Error(`Server error (${response.status}). Details: ${errorText}`);
        }

        throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
      }

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get headers with JWT token injection
   */
  getHeaders: () => {
    const token = authService.getToken();
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  },
};

export default httpClient;
